import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Row,
  Col,
  InputGroup,
  FormText,
  Button,
  Table,
  Dropdown,
  DropdownButton,
} from "react-bootstrap";
import {
  IconUser,
  IconFileDescription,
  IconCalendarWeek,
  IconTrash,
  IconCurrencyDollar,
} from "@tabler/icons-react";
import { useFormContext, Controller, useFieldArray } from "react-hook-form";
import DatePicker from "react-datepicker";
import usePostRequest from "../../hooks/usePostRequest";
import { API_ENDPOINTS } from "../../constant/_constant";

function GeneralForm({ isEditing, onOrderIDBlur }) {
  const {
    control,
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext();

  const rmaTypeSelected = watch("rmaType");

  const {
    data: fetchdatas,
    isLoadings,
    postData: fetchRfq,
  } = usePostRequest(API_ENDPOINTS.GET_CUSTOMER_AUTOFILL);

  // Custom Fields State and Logic
  const {
    fields: customFields,
    append: appendField,
    remove: removeField,
  } = useFieldArray({
    control,
    name: "custom_fields",
  });

  const [fieldType, setFieldType] = useState("");
  const [fieldName, setFieldName] = useState("");
  const [customFieldError, setCustomFieldError] = useState("");
  const suppressAutoUpdate = useRef(false);
  const [searchDebounces, setSearchDebounces] = useState({
    searchName: "",
  });
  const [suggestionData, setSuggestionData] = useState([]);

  const suggestionBoxRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionBoxRef.current &&
        !suggestionBoxRef.current.contains(event.target)
      ) {
        setSuggestionData(null);
        setSearchDebounces({ searchName: "" });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleAddCustomField = () => {
    if (!fieldType || !fieldName.trim()) {
      setCustomFieldError("Both field type and name are required.");
      return;
    }

    appendField({
      type: fieldType,
      field_name: fieldName.trim(),
      value: "",
    });

    // Clear state
    setFieldName("");
    setFieldType("");
    setCustomFieldError("");
  };

  const handleRemoveCustomField = (index) => {
    removeField(index);
  };

  const handleCancelCustomField = () => {
    setFieldType("");
    setFieldName("");
  };

  // useEffect(() => {
  //   if (!rmaTypeSelected) {
  //     setValue("rmaType", "EXCHANGE");
  //   }
  // }, [setValue, rmaTypeSelected]);

  const handleSearchByName = (e) => {
    setSearchDebounces({ searchName: e.target.value });
  };

  useEffect(() => {
    if (searchDebounces?.searchName?.length > 0) {
      fetchRfq(
        {
          first_name:
            searchDebounces?.searchName?.length > 0
              ? searchDebounces?.searchName
              : undefined,
          module_type: "ORDER",
        },
        onSuggestionSuccessData,
      );
    }
  }, [searchDebounces]);

  const onSuggestionSuccessData = (data) => {
    setSuggestionData(data);
  };

  const handleSelectOrganization = (item) => {
    suppressAutoUpdate.current = true;

    setValue("organization_name", item.display_name);

    setValue(
      "contractOfficerName",
      `${item?.first_name || ""} ${item?.last_name || ""}`,
    );
    setValue(
      "contractOfficerEmail",
      item?.primary_email ? item.primary_email.Address : "",
    );
    setValue(
      "contractOfficerPhone",
      item?.primary_phone ? item.primary_phone.FreeFormNumber : "",
    );
    setValue("contractOfficerAddress", item.address);

    setValue("shippingCustomerName", item?.shipping_name);
    setValue("shippingCustomerEmail", item?.shipping_email);
    setValue("shippingCustomerPhone", item?.shipping_phone);

    if (item?.shipping_address) {
      const address = item.shipping_address;
      const formattedAddress = [
        address.Line1,
        address.Line2,
        `${address.City || ""}${address.City ? "," : ""} ${
          address.CountrySubDivisionCode || ""
        } ${address.PostalCode || ""}`.trim(),
        address.Country,
      ]
        .filter((line) => line && line.trim() !== "") // Remove blank lines
        .join("\n");
      console.log("formattedAddress", formattedAddress);
      setValue("shippingCustomerAddress", formattedAddress);
    }

    setValue("BillingCustomerName", item?.billing_name);
    setValue("BillingCustomerEmail", item?.billing_email);
    setValue("BillingCustomerPhone", item?.billing_phone);

    if (item?.billing_address) {
      const address = item.billing_address;
      const formattedAddress = [
        address.Line1,
        address.Line2,
        `${address.City || ""}${address.City ? "," : ""} ${
          address.CountrySubDivisionCode || ""
        } ${address.PostalCode || ""}`.trim(),
        address.Country,
      ]
        .filter((line) => line && line.trim() !== "")
        .join("\n");
      setValue("BillingCustomerAddress", formattedAddress);
    }
    setSuggestionData(null);
  };

  return (
    <>
      <Row className="mb-3 mt-3">
        <Form.Group as={Col}>
          <div style={{ display: "flex", gap: "2px 18px", flexWrap: "wrap" }}>
            <Form.Label className="w-md-auto" style={{ fontWeight: "bold" }}>
              RMA Type:
            </Form.Label>

            <Form.Check>
              <Form.Check.Input
                type="radio"
                id="RMAIdExchange"
                value="EXCHANGE"
                {...register("rmaType")}
              />
              <Form.Check.Label htmlFor="RMAIdExchange">
                Exchange
              </Form.Check.Label>
            </Form.Check>

            <Form.Check>
              <Form.Check.Input
                type="radio"
                id="RMAIdReturn"
                value="RETURN"
                {...register("rmaType")}
              />
              <Form.Check.Label htmlFor="RMAIdReturn">Return</Form.Check.Label>
            </Form.Check>
          </div>
        </Form.Group>
      </Row>
      <Row className="">
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label className="">Order ID</Form.Label>
            <InputGroup>
              <Form.Control
                {...register("orderId")}
                placeholder="Enter Order ID"
                onBlur={(e) => {
                  register("orderId").onBlur(e);
                  onOrderIDBlur?.();
                }}
                disabled={isEditing}
              />
            </InputGroup>
            {errors.orderId && (
              <span className="text-danger">{errors.orderId.message}</span>
            )}
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label className="required">RMA ID</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <IconUser stroke={1} />
              </InputGroup.Text>
              <Form.Control
                {...register("rma_id")}
                placeholder="Enter RMA ID"
                defaultValue={"N/A"}
                disabled={isEditing}
              />
            </InputGroup>
            {errors.rma_id && (
              <span className="text-danger">{errors.rma_id.message}</span>
            )}
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label className="">Manufacturer RMA ID</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <IconUser stroke={1} />
              </InputGroup.Text>
              <Form.Control
                {...register("manufacturer_id")}
                placeholder="Enter Manufacturer's RMA ID"
              />
            </InputGroup>
            {errors.manufacturer_id && (
              <span className="text-danger">
                {errors.manufacturer_id.message}
              </span>
            )}
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Organization/Agency Name</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <IconUser stroke={1} />
              </InputGroup.Text>
              <Form.Control
                style={{ textTransform: "capitalize" }}
                {...register("organization_name")}
                placeholder="Enter agency Name"
                onChange={handleSearchByName}
              />
            </InputGroup>
            <div>
              {suggestionData?.data?.length > 0 &&
                searchDebounces?.searchName?.length > 0 && (
                  <div className="suggestions_box" ref={suggestionBoxRef}>
                    <div className="table-responsive">
                      <Table>
                        <tbody>
                          {suggestionData?.data?.map((item, index) => (
                            <tr
                              key={index}
                              onClick={() => handleSelectOrganization(item)}
                              style={{ cursor: "pointer" }}
                            >
                              <td>{item?.display_name}</td>
                              <td>{`${item?.first_name || ""}`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
            </div>
            {errors.organization_name && (
              <span className="text-danger">
                {errors.organization_name.message}
              </span>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>RMA Description</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <IconFileDescription stroke={1} />
              </InputGroup.Text>
              <Form.Control
                as="textarea"
                {...register("rma_Description")}
                placeholder="Enter Description"
                style={{
                  height: "auto",
                  minHeight: "80px",
                  resize: "none",
                }}
                onInput={(e) => {
                  e.target.style.height = "auto";
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
              />
            </InputGroup>
            {errors.rma_Description && (
              <span className="text-danger">
                {errors.rma_Description.message}
              </span>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row className="">
        {/* <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Tracking Number</Form.Label>
            <InputGroup>
              <Form.Control
                type="text" // Changed to text to support alphanumeric tracking numbers
                {...register("trackingID")}
                placeholder="Enter Tracking Number"
              />
            </InputGroup>
            {errors.trackingID && (
              <span className="text-danger">{errors.trackingID.message}</span>
            )}
          </Form.Group>
        </Col> */}
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label className="required">Date Reported</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <IconCalendarWeek stroke={1} />
              </InputGroup.Text>
              <Controller
                name="publishedDate"
                control={control}
                defaultValue={new Date()}
                rules={{ required: "Published date is required" }}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    selected={field.value ? new Date(field.value) : new Date()}
                    onChange={(date) => field.onChange(date)}
                    dateFormat="MM/dd/yyyy"
                    // minDate={new Date()}
                    placeholderText="Select Date"
                    className="form-control required"
                    yearDropdownItemNumber={20}
                    scrollableYearDropdown
                    showYearDropdown
                    showMonthDropdown
                  />
                )}
              />
            </InputGroup>
            {errors.publishedDate && (
              <span className="text-danger">
                {errors.publishedDate.message}
              </span>
            )}
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label className="">Estimated Return Date</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <IconCalendarWeek stroke={1} />
              </InputGroup.Text>
              <Controller
                name="estimatedDate"
                control={control}
                // rules={{ required: "Estimated Return date is required" }}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    selected={field.value ? new Date(field.value) : null}
                    onChange={(date) => field.onChange(date)}
                    dateFormat="MM/dd/yyyy"
                    // minDate={new Date()}
                    placeholderText="Select Date"
                    className="form-control required"
                    yearDropdownItemNumber={20}
                    scrollableYearDropdown
                    showYearDropdown
                    showMonthDropdown
                  />
                )}
              />
            </InputGroup>
            {errors.estimatedDate && (
              <span className="text-danger">
                {errors.estimatedDate.message}
              </span>
            )}
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label className="">Actual Return Date</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <IconCalendarWeek stroke={1} />
              </InputGroup.Text>
              <Controller
                name="actualDate"
                control={control}
                rules={{ required: "Actual Return date is required" }}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    selected={field.value ? new Date(field.value) : null}
                    onChange={(date) => field.onChange(date)}
                    dateFormat="MM/dd/yyyy"
                    // minDate={new Date()}
                    placeholderText="Select Date"
                    className="form-control required"
                    yearDropdownItemNumber={20}
                    scrollableYearDropdown
                    showYearDropdown
                    showMonthDropdown
                  />
                )}
              />
            </InputGroup>
            {errors.actualDate && (
              <span className="text-danger">{errors.actualDate.message}</span>
            )}
          </Form.Group>
        </Col>

        {customFields.length > 0 &&
          customFields.map((field, index) => (
            <Col md={4} key={index}>
              <Form.Group className="mb-3">
                <div className="d-flex align-items-center justify-content-between">
                  <Form.Label>{field.field_name}</Form.Label>
                  <span
                    className="trash_icon"
                    onClick={() => handleRemoveCustomField(index)}
                  >
                    <IconTrash size={20} stroke={1} />
                  </span>
                </div>
                {field.type === "number" && (
                  <Form.Control
                    type="number"
                    placeholder={`Enter ${field.field_name}`}
                    {...register(`custom_fields[${index}].value`)}
                  />
                )}
                {field.type === "text" && (
                  <Form.Control
                    type="text"
                    placeholder={`Enter ${field.field_name}`}
                    {...register(`custom_fields[${index}].value`)}
                  />
                )}
                {field.type === "date" && (
                  <InputGroup>
                    <InputGroup.Text>
                      <IconCalendarWeek stroke={1} />
                    </InputGroup.Text>
                    <Controller
                      name={`custom_fields[${index}].value`}
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          selected={field.value ? new Date(field.value) : null}
                          onChange={(date) => field.onChange(date)}
                          dateFormat="MM/dd/yyyy"
                          minDate={new Date()}
                          placeholderText={`Select ${
                            field.field_name || "Date"
                          }`}
                          className="form-control"
                          showMonthDropdown
                          showYearDropdown
                        />
                      )}
                    />
                  </InputGroup>
                )}
                {errors.custom_fields?.[index]?.value && (
                  <span className="text-danger">
                    {errors.custom_fields[index].value.message}
                  </span>
                )}
              </Form.Group>
            </Col>
          ))}
      </Row>
      <Row>
        {/* <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label className="required">Product/Service</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                {...register("productService")}
                placeholder="Enter product / service"
              />
            </InputGroup>
            {errors.productService && (
              <span className="text-danger">
                {errors.productService.message}
              </span>
            )}
          </Form.Group>
        </Col> */}
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label className="">Purchase Order</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                {...register("purchaseOrder")}
                placeholder="Enter Purchase Order"
              />
            </InputGroup>
            {errors.purchaseOrder && (
              <span className="text-danger">
                {errors.purchaseOrder.message}
              </span>
            )}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label className="">Initial Cause</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                {...register("initialCause")}
                placeholder="Enter Initial Cause"
              />
            </InputGroup>
            {errors.initialCause && (
              <span className="text-danger">{errors.initialCause.message}</span>
            )}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label className="">Total Cost</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <IconCurrencyDollar stroke={2} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                {...register("totalCostAfterMargin")}
                placeholder="Enter Total Cost"
              />
            </InputGroup>
            {errors.initialCause && (
              <span className="text-danger">{errors.initialCause.message}</span>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={9}>
          <Form.Group className="mb-3">
            <Form.Label>Custom Fields</Form.Label>
            <InputGroup className="custom-dropdown mb-3">
              <DropdownButton
                variant="outline-secondary"
                title={fieldType || "Select Type"}
                id="input-group-dropdown-1"
                onSelect={(type) => setFieldType(type)}
              >
                <Dropdown.Item eventKey="number">Number</Dropdown.Item>
                <Dropdown.Item eventKey="text">Text</Dropdown.Item>
                <Dropdown.Item eventKey="date">Date</Dropdown.Item>
              </DropdownButton>
              <Form.Control
                aria-label="Field Name"
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                placeholder="Enter field name"
              />
            </InputGroup>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Label className="form-label d-none d-sm-block">
            &nbsp;
          </Form.Label>
          <div className="d-flex gap-2">
            <Button
              className="flex-grow-1"
              onClick={handleAddCustomField}
              variant="success"
            >
              Add
            </Button>
            <Button
              className="flex-grow-1"
              variant="outline-secondary"
              onClick={handleCancelCustomField}
            >
              Cancel
            </Button>
          </div>
        </Col>
      </Row>
      {customFieldError && (
        <span className="text-danger mt-1">{customFieldError}</span>
      )}
    </>
  );
}
export default GeneralForm;
